import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { PillList } from 'src/core/ui/PillList';
import { PillItemProps } from 'src/core/ui/PillList/PillItem';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { sortRowsByOrdinal } from 'src/utils/venueUtils';
import { Feature, RowInfo, SectionInfo } from 'src/WebApiController';

import { FilterPillAbsoluteInput } from './FilterPillAbsoluteInput';
import { ROW_FILTER_MISSING } from './FilterPillList.constants';
import * as styles from './FilterPillList.css';
import {
  getRowIdsFromRowRange,
  getRowRangeFromRowIds,
} from './FilterPillList.utils';
import { MismatchedSectionDisclaimer } from './PillItemMismatchedDisclaimer';
import { RowIdFilterDropdown } from './RowIdFilterDropdown';

type FilterPillListProps = {
  disabled?: boolean;
  selectedSections: SectionInfo[];
  animatedSectionIds?: number[];
  rowIdFilters?: number[] | undefined;
  sectionRowIdFilters?: { [key: string]: string } | undefined;
  useCondensedBorders?: boolean;
  scrollButtonClassName?: Record<'up' | 'down' | 'left' | 'right', string>;
  sectionOnly?: boolean;
  allowRowRange?: boolean;
  applyRowFilterChanges: (
    rowIdFiltersNew: number[],
    sectionRowIdFiltersNew: {
      [key: string]: string;
    }
  ) => void;

  applySelectedSectionChanges: (
    sectionId: number,
    selectedSections: SectionInfo[]
  ) => void;
};

type RowOverride = RowInfo & {
  isManualOverride: boolean;
};

export const FilterPillList = ({
  disabled,
  selectedSections,
  rowIdFilters,
  animatedSectionIds,
  sectionRowIdFilters,
  useCondensedBorders,
  scrollButtonClassName,
  sectionOnly,
  allowRowRange,
  applyRowFilterChanges,
  applySelectedSectionChanges,
}: FilterPillListProps) => {
  const [focusedSections, setFocusedSections] = useState<SectionInfo[]>([]);

  const [keysPressed, setKeysPressed] = useState<string[]>([]);
  const [unmatchedSections, setUnmatchedSections] = useState<number[]>([]);

  const { activeConfigOverride } = useEventMapContext();
  const hasFallbackRowFilterWithOrdinalRankFeature = useUserHasFeature(
    Feature.FallbackRowFilterWithOrdinalRank
  );

  useEffect(() => {
    if (sectionOnly) {
      setUnmatchedSections([]);
    }
  }, [sectionOnly]);

  // Backfill the sections with newly added rows from score override
  const backfilledSections = useMemo(() => {
    if (!activeConfigOverride?.scoreOverrides) {
      return selectedSections;
    }
    const sectionToScoredRows =
      activeConfigOverride?.scoreOverrides?.reduce(
        (acc, score) => {
          acc[score.sectionId] = acc[score.sectionId] || [];
          if (score.rowOrdinal != null && score.rowName != null) {
            acc[score.sectionId].push({
              id: score.rowId,
              name: score.rowName,
              ordinal: score.rowOrdinal,
              tktClass: score.tkClsId,
              isManualOverride: score.isManualOverride,
            } as RowOverride);
          }
          return acc;
        },
        {} as Record<number, RowOverride[]>
      ) ?? {};

    const sanitized: SectionInfo[] = [];
    for (const section of selectedSections) {
      const venueRows = section.rows;
      if (sectionToScoredRows[section.id]) {
        for (const { isManualOverride, ...scoredRow } of sectionToScoredRows[
          section.id
        ]) {
          const index = venueRows.findIndex((row) => row.id === scoredRow.id);
          if (index < 0) {
            // New row from score override
            venueRows.push(scoredRow);
          } else {
            // override section rows from override
            if (isManualOverride) {
              venueRows[index] = scoredRow;
            }
          }
        }
      }
      sanitized.push({ ...section, rows: venueRows });
    }
    return sanitized;
  }, [activeConfigOverride?.scoreOverrides, selectedSections]);

  const updateRowFiltersFromShortcut = useCallback(
    (rowFilter: string) => {
      if (rowFilter == null || rowFilter.length === 0) {
        return;
      }
      let newRowIdFilters = rowIdFilters ?? [];

      const newSectionRowIdFilters = {
        ...sectionRowIdFilters,
      };

      focusedSections.forEach((section) => {
        const matchingRow = section.rows.find(
          ({ name }) => name?.toLowerCase() === rowFilter.toLowerCase()
        );
        // Remove all the row filters associated with the section id
        const sectionRowIds = section.rows.map((r) => r.id);
        newRowIdFilters = [
          ...newRowIdFilters.filter((rowId) => !sectionRowIds.includes(rowId)),
        ];

        if (matchingRow !== undefined) {
          // Only store the highest ordinal row id to display in the pill
          newSectionRowIdFilters[section.id] = matchingRow.id.toString();

          // Exclude all rows from section if the matching row has no ordinal
          if (matchingRow.ordinal != null) {
            // Push all the row ids with an ordinal less than or equal to the matching row
            section.rows.forEach((row) => {
              if (row.ordinal == null) return;
              if (row.ordinal <= matchingRow.ordinal!) {
                newRowIdFilters.push(row.id);
              }
            });
          }
        } else {
          delete newSectionRowIdFilters[section.id];
        }
      });

      applyRowFilterChanges(newRowIdFilters, newSectionRowIdFilters);
      setKeysPressed([]);
      setFocusedSections((prev) => {
        return prev.filter((s) => !(s.id in newSectionRowIdFilters));
      });
      setUnmatchedSections([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowIdFilters, sectionRowIdFilters, backfilledSections, focusedSections]
  );

  const updateRowFiltersFromRowSelection = useCallback(
    (row: RowInfo, section: SectionInfo) => {
      let newRowIdFilters = rowIdFilters ?? [];

      const newSectionRowIdFilters = {
        ...sectionRowIdFilters,
      };

      const sortedSourceRows = sortRowsByOrdinal(section.rows);
      // the ordinal ranking position of the selected row against all the rows
      const rankedOrdinalIndex = sortedSourceRows.findIndex(
        ({ id }) => row.id === id
      );

      focusedSections.forEach((s) => {
        // Remove all the row filters associated with the section id
        const sectionRowIds = s.rows.map((r) => r.id);
        newRowIdFilters = [
          ...newRowIdFilters.filter((rowId) => !sectionRowIds.includes(rowId)),
        ];

        // get matched row from id or name
        const matchingRow = s.rows.find(
          ({ id, name }) =>
            id === row.id ||
            name?.toLowerCase() === (row.name ?? '').toLowerCase()
        );
        if (matchingRow !== undefined) {
          // Only store the highest ordinal row id to display in the pill
          newSectionRowIdFilters[s.id] = matchingRow.id.toString();

          // Exclude all rows from section if the matching row has no ordinal
          if (matchingRow.ordinal != null) {
            // Push all the row ids with an ordinal less than or equal to the matching row
            s.rows.forEach((row) => {
              if (row.ordinal == null) return;
              if (row.ordinal <= matchingRow.ordinal!) {
                newRowIdFilters.push(row.id);
              }
            });
          }
          return;
        }

        // if no matching row, use row with closest lower oridinal
        if (
          rankedOrdinalIndex != null &&
          hasFallbackRowFilterWithOrdinalRankFeature
        ) {
          // row with closest low oridinal rank
          const sortedRows = sortRowsByOrdinal(s.rows);
          const filteredRows = sortedRows.filter(
            (_, index) => index <= rankedOrdinalIndex
          );

          if (filteredRows && filteredRows.length > 0) {
            const lastRow = filteredRows[filteredRows.length - 1];
            newSectionRowIdFilters[s.id] = lastRow.id.toString();
            filteredRows.forEach((row) => newRowIdFilters.push(row.id));
          }
          return;
        }

        delete newSectionRowIdFilters[s.id];
      });

      applyRowFilterChanges(newRowIdFilters, newSectionRowIdFilters);
      setKeysPressed([]);
      setFocusedSections((prev) => {
        return prev.filter((s) => !(s.id in newSectionRowIdFilters));
      });
      setUnmatchedSections([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rowIdFilters,
      sectionRowIdFilters,
      backfilledSections,
      focusedSections,
      hasFallbackRowFilterWithOrdinalRankFeature,
    ]
  );

  useEffect(() => {
    const sectionsMissingRowFilter = backfilledSections.filter(
      (s) => sectionRowIdFilters && !(s.id in sectionRowIdFilters)
    );

    setFocusedSections(sectionsMissingRowFilter);
  }, [backfilledSections, sectionRowIdFilters]);

  useEffect(() => {
    let keyPressTimeout: NodeJS.Timeout;

    const handleKeyDown = (event: KeyboardEvent) => {
      const ignoreElements = ['INPUT', 'TEXTAREA', 'SELECT'];
      if (
        document.activeElement &&
        ignoreElements.includes(document.activeElement.tagName)
      ) {
        return;
      }

      if (!backfilledSections) return;

      if (!event.ctrlKey && /^[a-zA-Z0-9]$/.test(event.key)) {
        setKeysPressed((prevKeys) => {
          const updatedKeys = [...prevKeys, event.key];

          clearTimeout(keyPressTimeout);

          keyPressTimeout = setTimeout(() => {
            const rowFilter = updatedKeys.join('').toLowerCase();
            updateRowFiltersFromShortcut(rowFilter);
          }, 600);

          return updatedKeys;
        });
      } else if (!event.ctrlKey && event.key === 'Enter') {
        setKeysPressed((prevKeys) => {
          const rowFilter = prevKeys.join('').toLowerCase();
          updateRowFiltersFromShortcut(rowFilter);
          clearTimeout(keyPressTimeout);
          return [];
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(keyPressTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backfilledSections, focusedSections]);

  const onUpdateRowRange = useCallback(
    (sectionId: number, lowerRowName: string, upperRowName: string) => {
      let newRowIdFilters = rowIdFilters ?? [];
      const unmatched: number[] = [];

      const newSectionRowIdFilters = {
        ...sectionRowIdFilters,
      };

      if (isEmpty(focusedSections)) {
        const currentSection = selectedSections.find(
          (section) => section.id === sectionId
        );
        if (currentSection) {
          const matchedRows = getRowIdsFromRowRange(currentSection.rows, [
            lowerRowName,
            upperRowName,
          ]);

          // Remove all the row filters associated with the section id
          const sectionRowIds = currentSection.rows.map((r) => r.id);
          newRowIdFilters = [
            ...newRowIdFilters.filter(
              (rowId) => !sectionRowIds.includes(rowId)
            ),
          ];
          if (matchedRows.matched && matchedRows.upperRowId) {
            // Only store the highest ordinal row id to display in the pill
            newSectionRowIdFilters[currentSection.id] =
              matchedRows.upperRowId.toString();
            newRowIdFilters.push(...matchedRows.rowIds);
          } else {
            unmatched.push(currentSection.id);
            delete newSectionRowIdFilters[currentSection.id];
          }
        }
      } else {
        focusedSections.forEach((section) => {
          const matchedRows = getRowIdsFromRowRange(section.rows, [
            lowerRowName,
            upperRowName,
          ]);

          // Remove all the row filters associated with the section id
          const sectionRowIds = section.rows.map((r) => r.id);
          newRowIdFilters = [
            ...newRowIdFilters.filter(
              (rowId) => !sectionRowIds.includes(rowId)
            ),
          ];
          if (matchedRows.matched && matchedRows.upperRowId) {
            // Only store the highest ordinal row id to display in the pill
            newSectionRowIdFilters[section.id] =
              matchedRows.upperRowId.toString();
            newRowIdFilters.push(...matchedRows.rowIds);
          } else {
            unmatched.push(section.id);
            delete newSectionRowIdFilters[section.id];
          }
        });
      }

      applyRowFilterChanges(newRowIdFilters, newSectionRowIdFilters);
      setUnmatchedSections(unmatched);
    },
    [
      applyRowFilterChanges,
      focusedSections,
      rowIdFilters,
      sectionRowIdFilters,
      selectedSections,
    ]
  );

  const onPillHover = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget == null) {
        return;
      }

      e.currentTarget.classList.add(styles.boldPillHover);
      if (useCondensedBorders) {
        e.currentTarget.classList.add(styles.condensedBorderRadius);
      }
    },
    [useCondensedBorders]
  );

  const onPillLeave = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (e.currentTarget == null) {
      return;
    }

    e.currentTarget.classList.remove(styles.boldPillHover);
  }, []);

  const onPillClick = useCallback(
    (pillId: number, e: React.MouseEvent<HTMLElement>) => {
      // If shift key is pressed, select all pills or unselect all of them if they are all selected
      if (e.shiftKey) {
        if (backfilledSections.length == focusedSections.length) {
          setFocusedSections([]);
        } else {
          setFocusedSections(backfilledSections);
        }
      } else {
        setFocusedSections((prevFocusedSections) => {
          const isActive = prevFocusedSections.some(
            (pill) => pill.id === pillId
          );
          if (isActive) {
            return prevFocusedSections.filter((pill) => pill.id !== pillId);
          } else {
            const newPill = backfilledSections.find(
              (section) => section.id === pillId
            );

            return newPill
              ? [...prevFocusedSections, newPill]
              : prevFocusedSections;
          }
        });
      }
    },
    [focusedSections.length, backfilledSections]
  );

  const filterSectionsPills = useMemo((): PillItemProps[] => {
    const rowLookup = sectionRowIdFilters ?? {};

    return backfilledSections
      .map((section) => {
        const rowFilter = rowLookup?.[section.id];
        const rowInfo =
          rowFilter != null && rowFilter !== ROW_FILTER_MISSING
            ? section.rows.find((r) => r.id.toString() == rowFilter)
            : undefined;

        const isFocusedPill = focusedSections.find((s) => s.id == section.id);
        const useAnimatedBorder =
          !isFocusedPill && animatedSectionIds?.includes(section.id);

        const selectedSectionRowIds = section.rows
          .filter((row) => rowIdFilters?.includes(row.id))
          .map((row) => row.id);
        const rowRange = getRowRangeFromRowIds(
          section.rows,
          selectedSectionRowIds
        );

        let selectedRow: RowInfo | undefined =
          allowRowRange && rowRange?.isRange ? undefined : rowInfo;
        if (isFocusedPill && selectedRow) {
          selectedRow = {
            ...selectedRow,
            name: keysPressed.join('') || selectedRow.name,
          };
        }

        const mismatched =
          allowRowRange && unmatchedSections.includes(section.id);

        return {
          value: section.id.toString(),
          rowFilter: rowFilter,
          useAnimatedBorder: useAnimatedBorder,
          display: section.name,
          customPillContainer: isFocusedPill
            ? clsx(styles.boldPill, {
                [styles.condensedBorderRadius]: useCondensedBorders,
                [styles.animatedBorder]: useAnimatedBorder,
                [styles.errorPill]: mismatched,
              })
            : undefined,
          useCondensedBorders: useCondensedBorders,
          icon: mismatched ? <MismatchedSectionDisclaimer /> : undefined,
          error: mismatched,
          postfixItem: (
            <RowIdFilterDropdown
              useAnimatedBorder={useAnimatedBorder}
              section={section}
              rowName={isFocusedPill ? keysPressed.join('') : undefined}
              row={selectedRow}
              focusedSectionIds={focusedSections.map((f) => f.id)}
              onChange={(row) => updateRowFiltersFromRowSelection(row, section)}
              onTriggerFocus={(sectionId) => {
                const pill = backfilledSections.find((s) => s.id === sectionId);
                if (pill) {
                  setFocusedSections((prev) => {
                    if (prev.some((s) => s.id === pill.id)) return prev;
                    return [...prev, pill];
                  });
                }
              }}
              disabled={false}
              rowRange={
                allowRowRange && rowRange
                  ? [rowRange.lowerRowId, rowRange.upperRowId]
                  : undefined
              }
              error={mismatched}
              contentFooter={
                allowRowRange && (
                  <FilterPillAbsoluteInput
                    sectionId={section.id}
                    rowRange={
                      rowRange
                        ? [rowRange.lowerRowId, rowRange.upperRowId]
                        : undefined
                    }
                    sectionRows={section.rows}
                    onUpdateRowRange={onUpdateRowRange}
                  />
                )
              }
            />
          ),
        };
      })
      .toSorted((a, b) => {
        // Always pull the errored pill to the first of the list
        if (a.error || b.error) {
          return a.error ? -1 : 1;
        }
        if (a.useAnimatedBorder || b.useAnimatedBorder)
          return a.useAnimatedBorder ? 1 : -1;
        // Pills without a row name (rowInfo) come first
        if (!a.rowFilter && b.rowFilter) {
          return -1;
        } else if (a.rowFilter && !b.rowFilter) {
          return 1;
        }

        // Otherwise just sort by the section name
        return a.display.localeCompare(b.display, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      })
      .map(
        ({
          value,
          display,
          postfixItem,
          useAnimatedBorder,
          customPillContainer,
          useCondensedBorders,
          icon,
          error,
        }) => ({
          value,
          display,
          useAnimatedBorder,
          postfixItem: sectionOnly ? undefined : postfixItem,
          customPillContainer,
          useCondensedBorders,
          icon,
          error,
        })
      );
  }, [
    sectionRowIdFilters,
    backfilledSections,
    focusedSections,
    animatedSectionIds,
    allowRowRange,
    unmatchedSections,
    useCondensedBorders,
    keysPressed,
    onUpdateRowRange,
    rowIdFilters,
    updateRowFiltersFromRowSelection,
    sectionOnly,
  ]);

  if (!filterSectionsPills.length) return null;

  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <PillList
        pills={filterSectionsPills}
        disabled={disabled}
        condensed={useCondensedBorders}
        scrollButtonClassName={scrollButtonClassName}
        onPillHover={onPillHover}
        onPillLeave={onPillLeave}
        onPillClick={onPillClick}
        onPillDeleted={(key) => {
          const sectionId = Number(key);
          if (!isNaN(sectionId)) {
            applySelectedSectionChanges(sectionId, backfilledSections);
          }
        }}
      />
    </div>
  );
};
